import { useEffect, useState, useCallback } from "react";
import * as qz from "qz-tray";
import PageHeader from "src/core/ui/PageHeader";
import Footer from "src/core/ui/helpers/Footer";
import ContentCardsContainer, { ContentCard } from "src/core/ui/ContentCard";

type PrintMode = "pdf" | "image" | "raw" | "zpl";
type PageOrientation = "portrait" | "landscape";

type DiagnosticSnapshot = {
  connected: boolean;
  qzLibraryVersion: string | null;
  qzTrayVersion: string | null;
  defaultPrinter: string | null;
  selectedPrinter: string | null;
  printerCount: number;
};

const DEFAULT_RAW_TEXT = `QZ Tray test print

If you can read this, raw printing from the ERP test page is working.

Test generated:
`;

const DEFAULT_ZPL = `^XA
^PW812
^LL600
^FO50,50
^A0N,50,50
^FDQZ TRAY TEST^FS
^FO50,130
^A0N,30,30
^FDPrinter connection successful^FS
^FO50,190
^BY3
^BCN,100,Y,N,N
^FD123456789^FS
^XZ`;

export function Printing() {
  const [logs, setLogs] = useState<string[]>([]);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [printers, setPrinters] = useState<string[]>([]);
  const [defaultPrinter, setDefaultPrinter] = useState<string | null>(null);
  const [selectedPrinter, setSelectedPrinter] = useState("");
  const [qzTrayVersion, setQzTrayVersion] = useState<string | null>(null);

  const [copies, setCopies] = useState(1);
  const [orientation, setOrientation] = useState<PageOrientation>("portrait");
  const [paperWidth, setPaperWidth] = useState("210");
  const [paperHeight, setPaperHeight] = useState("297");
  const [rawText, setRawText] = useState(DEFAULT_RAW_TEXT);
  const [zpl, setZpl] = useState(DEFAULT_ZPL);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFileData, setSelectedFileData] = useState<string | null>(null);
  const [printMode, setPrintMode] = useState<PrintMode>("pdf");
  const [busy, setBusy] = useState(false);

  const addLog = useCallback((msg: string) => {
    setLogs((prev) => [`[${new Date().toLocaleTimeString()}] ${msg}`, ...prev]);
  }, []);

  const getErrorMessage = (error: unknown) => {
    if (error instanceof Error) return error.message;
    if (typeof error === "string") return error;

    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  };

  const ensureConnected = useCallback(() => {
    if (!qz.websocket.isActive()) {
      throw new Error("QZ Tray is not connected");
    }
  }, []);

  const refreshConnectionState = useCallback(() => {
    const isConnected = qz.websocket.isActive();
    setConnected(isConnected);
    return isConnected;
  }, []);

  const connect = useCallback(async () => {
    if (qz.websocket.isActive()) {
      setConnected(true);
      addLog("QZ Tray is already connected");
      return;
    }

    setConnecting(true);
    addLog("Connecting to QZ Tray on localhost...");

    try {
      await qz.websocket.connect({
        retries: 3,
        delay: 1,
      });

      setConnected(true);
      addLog("Connected to QZ Tray");

      try {
        const version = await qz.api.getVersion();
        setQzTrayVersion(version);
        addLog(`QZ Tray version: ${version}`);
      } catch (error) {
        addLog(`Connected, but could not read QZ Tray version: ${getErrorMessage(error)}`);
      }
    } catch (error) {
      setConnected(false);
      addLog(`Connection failed: ${getErrorMessage(error)}`);
    } finally {
      setConnecting(false);
    }
  }, [addLog]);

  const disconnect = useCallback(async () => {
    if (!qz.websocket.isActive()) {
      setConnected(false);
      addLog("QZ Tray was already disconnected");
      return;
    }

    try {
      await qz.websocket.disconnect();
      setConnected(false);
      addLog("Disconnected from QZ Tray");
    } catch (error) {
      addLog(`Disconnect failed: ${getErrorMessage(error)}`);
    }
  }, [addLog]);

  const getVersion = useCallback(async () => {
    try {
      ensureConnected();
      const version = await qz.api.getVersion();
      setQzTrayVersion(version);
      addLog(`QZ Tray version: ${version}`);
    } catch (error) {
      addLog(`Could not get QZ Tray version: ${getErrorMessage(error)}`);
    }
  }, [addLog, ensureConnected]);

  const findPrinters = useCallback(async () => {
    setBusy(true);
    addLog("Requesting installed printers...");

    try {
      ensureConnected();

      const found = await qz.printers.find();
      const printerList = Array.isArray(found) ? found : [found];

      setPrinters(printerList);
      addLog(`Found ${printerList.length} printer(s)`);

      printerList.forEach((printer) => addLog(`Printer: ${printer}`));

      if (!selectedPrinter && printerList.length > 0) {
        setSelectedPrinter(printerList[0]);
      }
    } catch (error) {
      addLog(`Printer discovery failed: ${getErrorMessage(error)}`);
    } finally {
      setBusy(false);
    }
  }, [addLog, ensureConnected, selectedPrinter]);

  const findDefaultPrinter = useCallback(async () => {
    setBusy(true);
    addLog("Requesting Windows default printer...");

    try {
      ensureConnected();

      const printer = await qz.printers.getDefault();
      setDefaultPrinter(printer);
      addLog(`Default printer: ${printer}`);

      if (!selectedPrinter) {
        setSelectedPrinter(printer);
      }
    } catch (error) {
      addLog(`Could not get default printer: ${getErrorMessage(error)}`);
    } finally {
      setBusy(false);
    }
  }, [addLog, ensureConnected, selectedPrinter]);

  const runDiscoveryTest = useCallback(async () => {
    setBusy(true);
    addLog("Starting connection + printer discovery test...");

    try {
      if (!qz.websocket.isActive()) {
        await qz.websocket.connect({
          retries: 3,
          delay: 1,
        });

        setConnected(true);
        addLog("PASS: QZ Tray connection");
      } else {
        addLog("PASS: QZ Tray already connected");
      }

      const version = await qz.api.getVersion();
      setQzTrayVersion(version);
      addLog(`PASS: QZ Tray version ${version}`);

      const found = await qz.printers.find();
      const printerList = Array.isArray(found) ? found : [found];

      setPrinters(printerList);
      addLog(`PASS: Found ${printerList.length} printer(s)`);

      const printer = await qz.printers.getDefault();
      setDefaultPrinter(printer);
      addLog(`PASS: Default printer is "${printer}"`);

      if (!selectedPrinter) {
        setSelectedPrinter(printer || printerList[0] || "");
      }

      addLog("Discovery test completed successfully");
    } catch (error) {
      addLog(`FAIL: Discovery test: ${getErrorMessage(error)}`);
    } finally {
      setBusy(false);
    }
  }, [addLog, selectedPrinter]);

  const getPrintConfig = useCallback(
    (extraOptions: Record<string, unknown> = {}) => {
      if (!selectedPrinter) {
        throw new Error("Select a printer first");
      }

      return qz.configs.create(selectedPrinter, {
        copies,
        ...extraOptions,
      });
    },
    [copies, selectedPrinter]
  );

  const printRawText = useCallback(async () => {
    setBusy(true);
    addLog(`Sending raw text to "${selectedPrinter}"...`);

    try {
      ensureConnected();

      const config = getPrintConfig();

      const data = [
        `${rawText}${new Date().toLocaleString()}\n\n\n`,
      ];

      await qz.print(config, data);
      addLog(`PASS: Raw text job sent to "${selectedPrinter}"`);
    } catch (error) {
      addLog(`Raw print failed: ${getErrorMessage(error)}`);
    } finally {
      setBusy(false);
    }
  }, [addLog, ensureConnected, getPrintConfig, rawText, selectedPrinter]);

  const printZpl = useCallback(async () => {
    setBusy(true);
    addLog(`Sending ZPL to "${selectedPrinter}"...`);

    try {
      ensureConnected();

      const config = getPrintConfig();

      await qz.print(config, [
        {
          type: "raw",
          format: "command",
          flavor: "plain",
          data: zpl,
        },
      ]);

      addLog(`PASS: ZPL job sent to "${selectedPrinter}"`);
    } catch (error) {
      addLog(`ZPL print failed: ${getErrorMessage(error)}`);
    } finally {
      setBusy(false);
    }
  }, [addLog, ensureConnected, getPrintConfig, selectedPrinter, zpl]);

  const printSimpleHtml = useCallback(async () => {
    setBusy(true);
    addLog(`Sending HTML test page to "${selectedPrinter}"...`);

    try {
      ensureConnected();

      const config = getPrintConfig({
        orientation,
        units: "mm",
        size: {
          width: Number(paperWidth),
          height: Number(paperHeight),
        },
      });

      const html = `
        <html>
          <body style="font-family: Arial, sans-serif; padding: 30px;">
            <h1>QZ Tray Test</h1>
            <p>This page was printed directly from the ERP QZ test page.</p>
            <hr />
            <p><strong>Printer:</strong> ${selectedPrinter}</p>
            <p><strong>Copies:</strong> ${copies}</p>
            <p><strong>Orientation:</strong> ${orientation}</p>
            <p><strong>Paper:</strong> ${paperWidth} × ${paperHeight} mm</p>
            <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
          </body>
        </html>
      `;

      await qz.print(config, [
        {
          type: "pixel",
          format: "html",
          flavor: "plain",
          data: html,
        },
      ]);

      addLog(`PASS: HTML test job sent to "${selectedPrinter}"`);
    } catch (error) {
      addLog(`HTML print failed: ${getErrorMessage(error)}`);
    } finally {
      setBusy(false);
    }
  }, [addLog, copies, ensureConnected, getPrintConfig, orientation, paperHeight, paperWidth, selectedPrinter]);

  const handleFileSelected = useCallback(async (file: File | null) => {
    setSelectedFile(file);
    setSelectedFileData(null);

    if (!file) {
      addLog("File selection cleared");
      return;
    }

    addLog(`Selected file: ${file.name} (${file.type || "unknown MIME type"}, ${file.size} bytes)`);

    if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
      setPrintMode("pdf");
    } else if (file.type.startsWith("image/")) {
      setPrintMode("image");
    }

    try {
      const result = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
          if (typeof reader.result !== "string") {
            reject(new Error("Could not read file as base64"));
            return;
          }

          const base64 = reader.result.includes(",") ? reader.result.split(",")[1] : reader.result;
          resolve(base64);
        };

        reader.onerror = () => reject(reader.error || new Error("Could not read file"));
        reader.readAsDataURL(file);
      });

      setSelectedFileData(result);
      addLog(`File loaded into browser: ${file.name}`);
    } catch (error) {
      addLog(`Could not read file: ${getErrorMessage(error)}`);
    }
  }, [addLog]);

  const printUploadedFile = useCallback(async () => {
    if (!selectedFile || !selectedFileData) {
      addLog("Select a PDF or image first");
      return;
    }

    setBusy(true);
    addLog(`Printing "${selectedFile.name}" to "${selectedPrinter}" as ${printMode}...`);

    try {
      ensureConnected();

      const config = getPrintConfig({
        orientation,
        units: "mm",
        size: {
          width: Number(paperWidth),
          height: Number(paperHeight),
        },
      });

      if (printMode === "pdf") {
        await qz.print(config, [
          {
            type: "pixel",
            format: "pdf",
            flavor: "base64",
            data: selectedFileData,
          },
        ]);
      } else if (printMode === "image") {
        await qz.print(config, [
          {
            type: "pixel",
            format: "image",
            flavor: "base64",
            data: selectedFileData,
          },
        ]);
      } else {
        throw new Error("Uploaded file printing only supports PDF and image mode");
      }

      addLog(`PASS: "${selectedFile.name}" sent to "${selectedPrinter}"`);
    } catch (error) {
      addLog(`File print failed: ${getErrorMessage(error)}`);
    } finally {
      setBusy(false);
    }
  }, [
    addLog,
    ensureConnected,
    getPrintConfig,
    orientation,
    paperHeight,
    paperWidth,
    printMode,
    selectedFile,
    selectedFileData,
    selectedPrinter,
  ]);

  const testPrinterAThenB = useCallback(async () => {
    if (printers.length < 2) {
      addLog("Two-printer test requires at least two installed printers");
      return;
    }

    setBusy(true);

    const printerA = printers[0];
    const printerB = printers[1];

    addLog(`Starting two-printer routing test: "${printerA}" then "${printerB}"`);

    try {
      ensureConnected();

      const configA = qz.configs.create(printerA, { copies: 1 });
      const configB = qz.configs.create(printerB, { copies: 1 });

      await qz.print(configA, [
        `QZ ROUTING TEST\n\nPrinter A\nExpected printer: ${printerA}\nGenerated: ${new Date().toLocaleString()}\n\n\n`,
      ]);

      addLog(`PASS: First job submitted to "${printerA}"`);

      await qz.print(configB, [
        `QZ ROUTING TEST\n\nPrinter B\nExpected printer: ${printerB}\nGenerated: ${new Date().toLocaleString()}\n\n\n`,
      ]);

      addLog(`PASS: Second job submitted to "${printerB}"`);
      addLog("Two-printer routing test complete");
    } catch (error) {
      addLog(`Two-printer test failed: ${getErrorMessage(error)}`);
    } finally {
      setBusy(false);
    }
  }, [addLog, ensureConnected, printers]);

  const copyDiagnostics = useCallback(async () => {
    const snapshot: DiagnosticSnapshot = {
      connected: qz.websocket.isActive(),
      qzLibraryVersion: typeof qz.version === "string" ? qz.version : null,
      qzTrayVersion,
      defaultPrinter,
      selectedPrinter: selectedPrinter || null,
      printerCount: printers.length,
    };

    const report = [
      "QZ Tray Printer Test",
      "====================",
      "",
      `Generated: ${new Date().toLocaleString()}`,
      `Browser: ${navigator.userAgent}`,
      "",
      `Connected: ${snapshot.connected ? "Yes" : "No"}`,
      `QZ JS library: ${snapshot.qzLibraryVersion || "Unknown"}`,
      `QZ Tray: ${snapshot.qzTrayVersion || "Unknown"}`,
      `Default printer: ${snapshot.defaultPrinter || "Unknown"}`,
      `Selected printer: ${snapshot.selectedPrinter || "None"}`,
      `Printer count: ${snapshot.printerCount}`,
      "",
      "Printers:",
      ...printers.map((printer) => `- ${printer}`),
      "",
      "Event log:",
      ...[...logs].reverse(),
    ].join("\n");

    try {
      await navigator.clipboard.writeText(report);
      addLog("Diagnostics copied to clipboard");
    } catch (error) {
      addLog(`Could not copy diagnostics: ${getErrorMessage(error)}`);
    }
  }, [addLog, defaultPrinter, logs, printers, qzTrayVersion, selectedPrinter]);

  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  useEffect(() => {
    refreshConnectionState();

    return () => {
      // Deliberately do not automatically disconnect here.
      // During development React StrictMode can mount/unmount twice,
      // which makes QZ connection testing harder to understand.
    };
  }, [refreshConnectionState]);

  return (
    <>
      <PageHeader title="QZ Tray Printing Test" />

      <ContentCardsContainer>
        <ContentCard cardId="Connection">
          <h3>1. QZ Tray Connection</h3>

          <div style={{ marginBottom: "12px" }}>
            <strong>Status:</strong>{" "}
            <span>{connected ? "Connected" : "Disconnected"}</span>
          </div>

          <div style={{ marginBottom: "12px" }}>
            <strong>QZ Tray version:</strong> {qzTrayVersion || "Unknown"}
          </div>

          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button disabled={connecting || connected} onClick={connect}>
              {connecting ? "Connecting..." : "Connect"}
            </button>

            <button disabled={!connected} onClick={disconnect}>
              Disconnect
            </button>

            <button disabled={!connected} onClick={getVersion}>
              Get Version
            </button>

            <button disabled={busy} onClick={runDiscoveryTest}>
              Run Full Discovery Test
            </button>
          </div>

          <p style={{ marginTop: "12px", fontSize: "0.85rem" }}>
            QZ Tray should be installed and running on this same computer. The normal connection uses localhost automatically.
          </p>
        </ContentCard>

        <ContentCard cardId="Printers">
          <h3>2. Printer Discovery</h3>

          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "16px" }}>
            <button disabled={!connected || busy} onClick={findPrinters}>
              Refresh Printers
            </button>

            <button disabled={!connected || busy} onClick={findDefaultPrinter}>
              Get Default Printer
            </button>
          </div>

          <div style={{ marginBottom: "12px" }}>
            <strong>Default printer:</strong> {defaultPrinter || "Unknown"}
          </div>

          <div style={{ marginBottom: "12px" }}>
            <label>
              <strong>Selected printer</strong>
              <br />
              <select
                value={selectedPrinter}
                onChange={(event) => setSelectedPrinter(event.target.value)}
                style={{ width: "100%", maxWidth: "500px", marginTop: "4px" }}
              >
                <option value="">Select printer...</option>

                {printers.map((printer) => (
                  <option key={printer} value={printer}>
                    {printer}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div>
            <strong>Installed printers ({printers.length})</strong>

            <div
              style={{
                marginTop: "6px",
                maxHeight: "180px",
                overflowY: "auto",
                border: "1px solid #ddd",
                padding: "8px",
              }}
            >
              {printers.length === 0 && <div>No printers loaded yet.</div>}

              {printers.map((printer) => (
                <div key={printer}>
                  {printer}
                  {printer === defaultPrinter ? " (default)" : ""}
                </div>
              ))}
            </div>
          </div>
        </ContentCard>

        <ContentCard cardId="PrintSettings">
          <h3>3. Common Print Settings</h3>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "12px" }}>
            <label>
              Copies
              <br />
              <input
                type="number"
                min={1}
                max={20}
                value={copies}
                onChange={(event) => setCopies(Math.max(1, Number(event.target.value) || 1))}
                style={{ width: "100%" }}
              />
            </label>

            <label>
              Orientation
              <br />
              <select
                value={orientation}
                onChange={(event) => setOrientation(event.target.value as PageOrientation)}
                style={{ width: "100%" }}
              >
                <option value="portrait">Portrait</option>
                <option value="landscape">Landscape</option>
              </select>
            </label>

            <label>
              Paper width (mm)
              <br />
              <input
                type="number"
                value={paperWidth}
                onChange={(event) => setPaperWidth(event.target.value)}
                style={{ width: "100%" }}
              />
            </label>

            <label>
              Paper height (mm)
              <br />
              <input
                type="number"
                value={paperHeight}
                onChange={(event) => setPaperHeight(event.target.value)}
                style={{ width: "100%" }}
              />
            </label>
          </div>

          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "16px" }}>
            <button
              onClick={() => {
                setPaperWidth("210");
                setPaperHeight("297");
                setOrientation("portrait");
              }}
            >
              A4 Portrait
            </button>

            <button
              onClick={() => {
                setPaperWidth("297");
                setPaperHeight("210");
                setOrientation("landscape");
              }}
            >
              A4 Landscape
            </button>

            <button
              onClick={() => {
                setPaperWidth("100");
                setPaperHeight("150");
                setOrientation("portrait");
              }}
            >
              100 × 150 Label
            </button>
          </div>
        </ContentCard>

        <ContentCard cardId="BasicPrintTests">
          <h3>4. Basic Printing Tests</h3>

          <p>
            These tests let the tester verify increasingly different printing paths.
          </p>

          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button disabled={!connected || !selectedPrinter || busy} onClick={printRawText}>
              Print Raw Text
            </button>

            <button disabled={!connected || !selectedPrinter || busy} onClick={printSimpleHtml}>
              Print HTML Test Page
            </button>

            <button disabled={!connected || printers.length < 2 || busy} onClick={testPrinterAThenB}>
              Test Printer 1 → Printer 2
            </button>
          </div>

          <p style={{ marginTop: "12px", fontSize: "0.85rem" }}>
            The two-printer test deliberately submits one job to the first discovered printer and another to the second. Use it only when both devices can safely accept plain/raw text.
          </p>
        </ContentCard>

        <ContentCard cardId="RawPrint">
          <h3>5. Raw Text Test</h3>

          <textarea
            value={rawText}
            onChange={(event) => setRawText(event.target.value)}
            rows={9}
            style={{ width: "100%", fontFamily: "monospace" }}
          />

          <div style={{ marginTop: "8px" }}>
            <button disabled={!connected || !selectedPrinter || busy} onClick={printRawText}>
              Send Raw Text to Selected Printer
            </button>
          </div>
        </ContentCard>

        <ContentCard cardId="ZplPrint">
          <h3>6. Zebra / ZPL Test</h3>

          <textarea
            value={zpl}
            onChange={(event) => setZpl(event.target.value)}
            rows={14}
            style={{ width: "100%", fontFamily: "monospace" }}
          />

          <div style={{ marginTop: "8px" }}>
            <button disabled={!connected || !selectedPrinter || busy} onClick={printZpl}>
              Send ZPL to Selected Printer
            </button>
          </div>

          <p style={{ marginTop: "12px", fontSize: "0.85rem" }}>
            Only use this against a printer that understands ZPL, such as a suitably configured Zebra printer.
          </p>
        </ContentCard>

        <ContentCard cardId="FilePrint">
          <h3>7. Upload and Print Document</h3>

          <div style={{ marginBottom: "12px" }}>
            <input
              type="file"
              accept=".pdf,image/*"
              onChange={(event) => handleFileSelected(event.target.files?.[0] || null)}
            />
          </div>

          {selectedFile && (
            <div style={{ marginBottom: "12px" }}>
              <div>
                <strong>File:</strong> {selectedFile.name}
              </div>
              <div>
                <strong>Size:</strong> {Math.round(selectedFile.size / 1024)} KB
              </div>
              <div>
                <strong>MIME:</strong> {selectedFile.type || "Unknown"}
              </div>
            </div>
          )}

          <div style={{ marginBottom: "12px" }}>
            <label>
              <strong>Interpret uploaded file as</strong>
              <br />
              <select
                value={printMode}
                onChange={(event) => setPrintMode(event.target.value as PrintMode)}
                style={{ width: "100%", maxWidth: "300px", marginTop: "4px" }}
              >
                <option value="pdf">PDF</option>
                <option value="image">Image</option>
              </select>
            </label>
          </div>

          <button
            disabled={!connected || !selectedPrinter || !selectedFile || !selectedFileData || busy}
            onClick={printUploadedFile}
          >
            Print Uploaded File to Selected Printer
          </button>

          <p style={{ marginTop: "12px", fontSize: "0.85rem" }}>
            This sends the selected PDF or image directly through QZ Tray to the selected printer. Chrome's print dialog is not involved.
          </p>
        </ContentCard>

        <ContentCard cardId="TestScenarios">
          <h3>8. Suggested Warehouse Test Scenarios</h3>

          <div style={{ marginBottom: "16px" }}>
            <strong>Picking list test</strong>
            <ol>
              <li>Select the normal A4 warehouse printer.</li>
              <li>Set A4 Portrait.</li>
              <li>Upload a real picking-list PDF.</li>
              <li>Click "Print Uploaded File to Selected Printer".</li>
            </ol>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <strong>Address label test</strong>
            <ol>
              <li>Select the Zebra printer.</li>
              <li>Click "100 × 150 Label".</li>
              <li>Use "Send ZPL to Selected Printer".</li>
            </ol>
          </div>

          <div>
            <strong>Printer routing test</strong>
            <ol>
              <li>Print the picking-list PDF to the A4 printer.</li>
              <li>Change the selected printer to Zebra.</li>
              <li>Immediately send the ZPL test.</li>
              <li>Confirm both jobs went to the correct physical devices without a Chrome dialog.</li>
            </ol>
          </div>
        </ContentCard>

        <ContentCard cardId="Diagnostics">
          <h3>9. Diagnostics</h3>

          <div style={{ marginBottom: "12px" }}>
            <button onClick={copyDiagnostics}>Copy Diagnostics</button>
            <button onClick={clearLogs} style={{ marginLeft: "8px" }}>
              Clear Log
            </button>
          </div>

          <pre
            style={{
              background: "#000",
              padding: "8px",
              fontSize: "0.8rem",
              maxHeight: "160px",
              overflowY: "auto",
            }}
          >
            {JSON.stringify(
              {
                connected,
                qzJsVersion: typeof qz.version === "string" ? qz.version : null,
                qzTrayVersion,
                defaultPrinter,
                selectedPrinter: selectedPrinter || null,
                printers,
                printSettings: {
                  copies,
                  orientation,
                  paperWidthMm: paperWidth,
                  paperHeightMm: paperHeight,
                },
                selectedFile: selectedFile
                  ? {
                      name: selectedFile.name,
                      size: selectedFile.size,
                      type: selectedFile.type,
                    }
                  : null,
              },
              null,
              2
            )}
          </pre>

          <h3>Event Log</h3>

          <div
            style={{
              maxHeight: "350px",
              overflowY: "auto",
              border: "1px solid #ddd",
              padding: "6px",
              fontSize: "0.8rem",
              background: "#000",
            }}
          >
            {logs.length === 0 && <div>No events yet.</div>}

            {logs.map((log, index) => (
              <div key={index}>{log}</div>
            ))}
          </div>
        </ContentCard>
      </ContentCardsContainer>

      <Footer />
    </>
  );
}

export default Printing;